<template>
    <div class="z-key-indicator" :class="classObject" ref="el">
        <z-icon :name="icon" class="z-key-indicator__icon" v-if="icon" />
        <div class="z-key-indicator__container">
            <div class="z-key-indicator__header" v-if="caption">
                <vue-raw class="z-key-indicator__caption" :raw="caption" />
            </div>
            <div class="z-key-indicator__body">
                <div class="z-key-indicator__value-wrapper">
                    <span
                        v-if="textBefore"
                        class="z-key-indicator__value-text z-key-indicator__value-text--before"
                        v-html="textBefore"
                    ></span>
                    <span
                        ref="value"
                        class="z-key-indicator__value"
                        :class="[{'z-key-indicator__value--small' : filteredValue.length > 10 }]"
                        v-html="filteredValue"
                    >
                    </span>
                    <span
                        v-if="textAfter"
                        class="z-key-indicator__value-text z-key-indicator__value-text--after"
                        v-html="textAfter"
                    ></span>
                </div>
                <div class="z-key-indicator__detail" v-if="detail || !!slots.detail">
                    <vue-raw :raw="detail" v-if="detail" />
                    <slot name="detail" v-if="!!slots.detail"/>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { useUtils } from '@/composable/useUtils'
import {computed, onBeforeUnmount, onMounted, ref, useSlots} from 'vue'
import verge from 'verge'
import { CountUp } from 'countup.js'
import {useGetter} from '@/composable/useGetter'

const { numberFilter } = useUtils()

const props = defineProps(
    {
        value: [String, Number],
        textBefore: String,
        textAfter: String,
        valueTextColumn: Boolean,
        valueTextCentered: {
            type: Boolean,
            default: true
        },
        modifyValue: {
            type: Boolean,
            default: true,
        },
        detail: {
            type: String
        },
        caption: {
            type: String
        },
        detailPosition: {
            type: String,
            default: 'bottom',
            validator: prop => ['bottom', 'right'].includes(prop)
        },
        icon: {
            type: String
        },
        iconCentered: {
            type: Boolean
        },
        detailCentered: {
            type: Boolean,
        },
        detailBordered: {
            type: Boolean,
            default: true
        },
        iconPosition: {
            type: String,
            default: 'top',
            validator: prop => ['top', 'left'].includes(prop)
        },
        size: {
            type: String,
            validator: (prop) => ['l', 'm'].includes(prop),
            default: 'm'
        },
        animation: {
            type: Boolean,
            default: false
        }
    }
)

const { lang } = useGetter(['lang'])

const speed = ref(2)
const counter = ref(null)
const isAnimated = ref(false)
const el = ref(null)
const slots = useSlots()

const getDecimals = () => {
    if (!props.value) return 0
    let value = Number.parseFloat(props.value.replace(',', '.')).toString()
    return value.includes('.') ? value.split('.').pop().length : 0
}

const inView = () => {
    return verge.inViewport(el.value)
}

const startAnimation = () => {
    if (inView() && counter.value) {
        counter.value.start()
        setTimeout(() => { isAnimated.value = true }, speed.value * 1000)
        window.removeEventListener('scroll', startAnimation)
    }
}

const options = computed(() => {
    return ({
        duration: speed.value,
        useEasing: true,
        useGrouping: true,
        decimalPlaces: getDecimals(),
        separator: lang.value === 'ru' ? '<span class="symbol"> </span>' : '',
        decimal: lang.value === 'ru' ? '<span class="symbol">,</span>' : '<span class="symbol">.</span>',
        prefix: '',
        suffix: ''
    })
})

const init = () => {
    if (!(props.animation && !isNaN(Number(props.value)))) return
    let value = Number.parseFloat(props.value.replace(',', '.')).toString()
    counter.value = new CountUp(el.value.getElementsByClassName('z-key-indicator__value')[0], value, options.value)
}

const classObject = computed(() => {
    return [
        `z-key-indicator--size-${props.size}`,
        `z-key-indicator--icon-position-${props.iconPosition}`,
        `z-key-indicator--detail-position-${props.detailPosition}`,
        { 'z-key-indicator--icon-centered': props.iconCentered },
        { 'z-key-indicator--detail-centered': props.detailCentered },
        { 'z-key-indicator--detail-bordered': props.detailBordered },
        { 'z-key-indicator--value-text-column': props.valueTextColumn },
        { 'z-key-indicator--value-centered': props.valueTextCentered }
    ]
})

const filteredValue = computed(() => {
    if (props.modifyValue) return numberFilter(props.value)
    return props.value
})


onMounted(() => {
    window.addEventListener('scroll', startAnimation)
    init()
    startAnimation()
})

onBeforeUnmount(() => {
    window.removeEventListener('scroll', startAnimation)
})

defineExpose({
    props
})
</script>

<style lang="scss" src="./index.scss"></style>
